import { useCallback } from 'react';
import useSWR from 'swr';
import { useAuth } from './useAuth';
import { apiClient } from '@/api';
import type {
  Connection,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  TestConnectionRequest,
  TestConnectionResponse,
} from '@/types';

interface ConnectionsResponse {
  connections: Connection[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

interface UseConnectionsOptions {
  limit?: number;
  offset?: number;
}

interface UseConnectionsResult {
  connections: Connection[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createConnection: (data: CreateConnectionRequest) => Promise<Connection>;
  updateConnection: (id: string, data: UpdateConnectionRequest) => Promise<Connection>;
  deleteConnection: (id: string) => Promise<void>;
  testConnection: (data: TestConnectionRequest) => Promise<TestConnectionResponse>;
  testExistingConnection: (id: string) => Promise<TestConnectionResponse>;
}

export function useConnections(options: UseConnectionsOptions = {}): UseConnectionsResult {
  const { session } = useAuth();
  const { limit = 50, offset = 0 } = options;

  const queryParams = new URLSearchParams();
  if (limit) queryParams.set('limit', String(limit));
  if (offset) queryParams.set('offset', String(offset));

  const endpoint = `/api/v1/connections?${queryParams.toString()}`;

  const fetcher = async (url: string): Promise<ConnectionsResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<ConnectionsResponse>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<ConnectionsResponse>(
    session?.access_token ? endpoint : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const createConnection = useCallback(async (reqData: CreateConnectionRequest): Promise<Connection> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    const result = await apiClient.post<Connection>(
      '/api/v1/connections',
      reqData,
      { token: session.access_token }
    );
    await mutate();
    return result;
  }, [session, mutate]);

  const updateConnection = useCallback(async (id: string, reqData: UpdateConnectionRequest): Promise<Connection> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    const result = await apiClient.put<Connection>(
      `/api/v1/connections/${id}`,
      reqData,
      { token: session.access_token }
    );
    await mutate();
    return result;
  }, [session, mutate]);

  const deleteConnection = useCallback(async (id: string): Promise<void> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    await apiClient.delete(`/api/v1/connections/${id}`, { token: session.access_token });
    await mutate();
  }, [session, mutate]);

  const testConnection = useCallback(async (reqData: TestConnectionRequest): Promise<TestConnectionResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.post<TestConnectionResponse>(
      '/api/v1/connections/test',
      reqData,
      { token: session.access_token }
    );
  }, [session]);

  const testExistingConnection = useCallback(async (id: string): Promise<TestConnectionResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.post<TestConnectionResponse>(
      `/api/v1/connections/${id}/test`,
      undefined,
      { token: session.access_token }
    );
  }, [session]);

  return {
    connections: data?.connections ?? [],
    total: data?.total ?? 0,
    hasMore: data?.has_more ?? false,
    isLoading,
    error: error ?? null,
    refresh,
    createConnection,
    updateConnection,
    deleteConnection,
    testConnection,
    testExistingConnection,
  };
}
